import { selectAuthUser } from 'store/selectors';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import { NavMenuTopLinks } from './NavMenuTopLinks';
import { NavMenuMiddleLinks } from './NavMenuMiddleLinks';
import { NavMenuChat } from './NavMenuChat';
import { NavMenuLanguage } from './NavMenuLanguage';
import { NavMenuLogout } from './NavMenuLogout';
import { useParams } from 'react-router-dom';
import { useContext } from 'react';
import { PreviewContext } from 'pages/ContentViewer/PreviewContext';
import { useAppTranslation } from 'services/i18n';
import { ContentViewerElementIndexResponseElementTypeEnum } from 'generated-api';

import ExitToAppIcon from '@mui/icons-material/ExitToApp';

interface Props {
  showTopMenu: boolean;
  mainContentType?: ContentViewerElementIndexResponseElementTypeEnum;
  setShowTestNotCompletedModal: (showTestNotCompletedModal: boolean) => void;
  isActiveTestAttempt: boolean;
  setShowTopMenu: (showTopMenu: boolean) => void;
}

const menuItemClassName =
  'tw-flex tw-h-52 tw-w-full tw-items-center tw-gap-20 tw-px-16 tw-pt-5 hover:tw-bg-black/5';

export const NavMenu = ({
  showTopMenu,
  mainContentType,
  setShowTestNotCompletedModal,
  isActiveTestAttempt,
  setShowTopMenu,
}: Props) => {
  const { stepId } = useParams();
  const t = useAppTranslation();
  const preview = useContext(PreviewContext);

  const authUserState = useSelector(selectAuthUser);
  const companySpaces = authUserState?.company_spaces;

  // TODO proper user & roles management
  const host = window.location.hostname.toLowerCase();
  const subdomain = host.split('.')[0];
  const userOrg = companySpaces?.find(
    (space) => space.custom_domain === host || space.subdomain === subdomain
  );
  const showDashboardLink = !!userOrg?.new_dashboard_on;
  const companySpaceSwitchingEnabled = userOrg?.custom_domain !== host;
  const isUserKindOfAdmin =
    !userOrg /* private space */ || userOrg.user_role === 'admin' || userOrg.user_role === 'owner';

  const handleAbandonedTest = (
    e?: React.MouseEvent<HTMLAnchorElement> | React.MouseEvent<HTMLButtonElement>
  ) => {
    const isLeavingTestInProgress = mainContentType === 'test' && stepId && isActiveTestAttempt;
    const blockNavigation = () => {
      e?.preventDefault();
      setShowTopMenu(false);
      setShowTestNotCompletedModal(true);
      return true;
    };
    if (e?.currentTarget instanceof HTMLAnchorElement) {
      const nextURL = e?.currentTarget.href;
      const urlStepId = nextURL?.split('/').pop();
      // disallow leaving test in progress
      if (isLeavingTestInProgress && stepId !== urlStepId) blockNavigation();
    }
    if (e?.currentTarget instanceof HTMLButtonElement && isLeavingTestInProgress) blockNavigation();
    return false;
  };

  const handleLeavePreview = () => {
    setShowTopMenu(false);
    preview?.leavePreview();
  };

  const sectionClassName = clsx(
    'tw-duration-400 tw-fixed tw-top-8 tw-left-8 tw-z-9999 tw-overflow-x-hidden tw-bg-white tw-shadow-2xl',
    // the short preview menu never overflows, so it must not show a permanent scrollbar
    preview?.isMarketplacePreview ? 'tw-overflow-y-auto' : 'tw-overflow-y-scroll',
    showTopMenu
      ? 'tw-w-256 tw-origin-top-left tw-scale-100 tw-opacity-100 tw-transition-all tw-ease-in'
      : 'tw-origin-top-left tw-scale-0 tw-overflow-hidden tw-opacity-0 tw-transition-all tw-ease-in'
  );
  const sectionStyle = { maxHeight: showTopMenu ? `calc(100dvh - 8px)` : 'auto' };

  // Marketplace preview must not offer any way out except back to the course card
  if (preview?.isMarketplacePreview) {
    return (
      <section className={sectionClassName} style={sectionStyle}>
        <div className="tw-border-b tw-border-lighterGray">
          <NavMenuLanguage />
          <NavMenuChat setShowTopMenu={setShowTopMenu} />
        </div>
        <button className={menuItemClassName} onClick={handleLeavePreview} type="button">
          <ExitToAppIcon style={{ color: '#757575' }} />
          {t('contentViewer.previewBar.leave')}
        </button>
      </section>
    );
  }

  return (
    <section className={sectionClassName} style={sectionStyle}>
      <NavMenuTopLinks
        isUserOrganization={userOrg !== undefined}
        showDashboardLink={showDashboardLink}
        isUserKindOfAdmin={isUserKindOfAdmin}
        onClick={handleAbandonedTest}
      />
      <NavMenuMiddleLinks
        onClick={handleAbandonedTest}
        companySpaceSwitchingEnabled={companySpaceSwitchingEnabled}
      />
      <NavMenuChat setShowTopMenu={setShowTopMenu} />
      <NavMenuLogout onClick={handleAbandonedTest} />
    </section>
  );
};
